import type { PartialRegenerateRequest } from '@/api/chapterApi'
import type { Asset, Chapter } from '@/types/nf'

export type QualityGateBand = 'excellent' | 'good' | 'review' | 'weak' | 'failed'

export interface QualityScore {
  score: number
  status?: string
  grade?: string
  layer?: string
  model?: string
  assetId?: string
  assetType?: Asset['asset_type']
  sourceCount?: number
  /** 评分后端失败时的可观测标记 (失败响亮化); 置位时 score 不具意义, UI 应显示「评分失败」。 */
  assessmentError?: { reason?: string; at?: string }
}

export interface ChapterQualityDimension {
  key: string
  label: string
  score: number
  grade?: string
  status: string
  layer: string
  model?: string
  notes?: string[]
}

export interface ChapterQualitySnapshot extends QualityScore {
  id: string
  chapter_id: string
  project_id: string
  branch_id: string
  chapter_number: number
  suggestions: string[]
  dimensions: ChapterQualityDimension[]
  created_at: number
}

export interface QualityGateTone {
  band: QualityGateBand
  color: string
  labelKey: string
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []
}

function parseDimensions(value: unknown): ChapterQualityDimension[] {
  if (!Array.isArray(value)) return []
  return value
    .filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object')
    .map((item) => ({
      key: typeof item.key === 'string' ? item.key : 'dimension',
      label: typeof item.label === 'string' ? item.label : 'Dimension',
      score: normalizeQualityScore(item.score) ?? 0,
      grade: typeof item.grade === 'string' ? item.grade : undefined,
      status: typeof item.status === 'string' ? item.status : 'unknown',
      layer: typeof item.layer === 'string' ? item.layer : '',
      model: typeof item.model === 'string' ? item.model : undefined,
      notes: stringArray(item.notes),
    }))
}

export function parseChapterQualitySnapshot(value: unknown): ChapterQualitySnapshot | null {
  if (!value || typeof value !== 'object') return null
  const record = value as Record<string, unknown>
  const score = normalizeQualityScore(record.score)
  if (score === null) return null
  const chapterID = typeof record.chapter_id === 'string' ? record.chapter_id : ''
  return {
    id: typeof record.id === 'string' ? record.id : `${chapterID}:${String(record.created_at ?? '')}`,
    chapter_id: chapterID,
    project_id: typeof record.project_id === 'string' ? record.project_id : '',
    branch_id: typeof record.branch_id === 'string' ? record.branch_id : 'main',
    chapter_number: typeof record.chapter_number === 'number' ? record.chapter_number : 0,
    score,
    status: typeof record.status === 'string' ? record.status : undefined,
    grade: typeof record.grade === 'string' ? record.grade : undefined,
    layer: typeof record.layer === 'string' ? record.layer : undefined,
    model: typeof record.model === 'string' ? record.model : undefined,
    suggestions: stringArray(record.suggestions),
    dimensions: parseDimensions(record.dimensions),
    created_at: typeof record.created_at === 'number' ? record.created_at : 0,
  }
}

function numericScore(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

export function normalizeQualityScore(value: unknown): number | null {
  const score = numericScore(value)
  if (score === null) return null
  const normalized = score >= 0 && score <= 1 ? score * 100 : score
  return Math.max(0, Math.min(100, Math.round(normalized)))
}

export function parseAssetQuality(asset: Asset | null | undefined): QualityScore | null {
  if (!asset?.payload?.trim()) return null
  try {
    const parsed = JSON.parse(asset.payload) as unknown
    if (!parsed || typeof parsed !== 'object') return null
    if ((parsed as { source_stale?: unknown }).source_stale) return null
    const rawQuality = (parsed as { quality?: unknown }).quality
    const rawScore =
      rawQuality && typeof rawQuality === 'object'
        ? (rawQuality as { score?: unknown }).score
        : rawQuality
    const score = normalizeQualityScore(rawScore)
    if (score === null) return null
    const qualityRecord = rawQuality && typeof rawQuality === 'object' ? (rawQuality as Record<string, unknown>) : {}
    return {
      score,
      status: typeof qualityRecord.status === 'string' ? qualityRecord.status : asset.status,
      grade: typeof qualityRecord.grade === 'string' ? qualityRecord.grade : undefined,
      layer: typeof qualityRecord.layer === 'string' ? qualityRecord.layer : undefined,
      assetId: asset.id,
      assetType: asset.asset_type,
      sourceCount: 1,
    }
  } catch {
    return null
  }
}

/** 从 chapter_text 资产解析评分失败标记 (后端 quality_error)。成功评分时该标记会被清除。 */
export function parseAssetAssessmentError(asset: Asset | null | undefined): QualityScore['assessmentError'] | null {
  if (!asset?.payload?.trim()) return null
  try {
    const parsed = JSON.parse(asset.payload) as unknown
    if (!parsed || typeof parsed !== 'object') return null
    const raw = (parsed as { quality_error?: unknown }).quality_error
    if (!raw || typeof raw !== 'object') return null
    const record = raw as Record<string, unknown>
    return {
      reason: typeof record.reason === 'string' ? record.reason : undefined,
      at: typeof record.at === 'string' ? record.at : undefined,
    }
  } catch {
    return null
  }
}

export function qualityGateTone(score: number): QualityGateTone {
  if (score >= 90) return { band: 'excellent', color: 'var(--gate-done)', labelKey: 'qualityGate.band.excellent' }
  if (score >= 70) return { band: 'good', color: 'var(--gate-running)', labelKey: 'qualityGate.band.good' }
  if (score >= 55) return { band: 'review', color: 'var(--gate-review)', labelKey: 'qualityGate.band.review' }
  if (score >= 40) return { band: 'weak', color: 'var(--gate-partial)', labelKey: 'qualityGate.band.weak' }
  return { band: 'failed', color: 'var(--gate-failed)', labelKey: 'qualityGate.band.failed' }
}

function assetSortKey(asset: Asset) {
  return `${String(asset.version ?? 0).padStart(8, '0')}:${String(asset.updated_at ?? '')}:${asset.id}`
}

export function chapterQualityFromAssets(chapterId: string, assets: Asset[]): QualityScore | null {
  const latestByType = new Map<Asset['asset_type'], Asset>()
  let latestChapterText: Asset | null = null
  for (const asset of assets) {
    if (asset.chapter_id !== chapterId) continue
    if (asset.asset_type === 'chapter_text' && (!latestChapterText || assetSortKey(asset) > assetSortKey(latestChapterText))) {
      latestChapterText = asset
    }
    const quality = parseAssetQuality(asset)
    if (!quality) continue
    const current = latestByType.get(asset.asset_type)
    if (!current || assetSortKey(asset) > assetSortKey(current)) latestByType.set(asset.asset_type, asset)
  }
  const chapterTextQuality = parseAssetQuality(latestChapterText)
  if (chapterTextQuality) return chapterTextQuality

  const scores = [...latestByType.values()]
    .filter((asset) => asset.asset_type !== 'chapter_text')
    .map((asset) => parseAssetQuality(asset))
    .filter((quality): quality is QualityScore => Boolean(quality))
  if (scores.length) {
    const score = Math.round(scores.reduce((sum, item) => sum + item.score, 0) / scores.length)
    return {
      score,
      sourceCount: scores.length,
    }
  }

  // 正文与下游均无分: 若 chapter_text 留有评分失败标记, 显式上抛 (失败响亮化),
  // 让 UI 区分「评分失败可重试」与「尚未评分」。
  const assessmentError = parseAssetAssessmentError(latestChapterText)
  if (assessmentError) {
    return { score: 0, assessmentError }
  }
  return null
}

export function buildChapterQualityRegenerateRequest(chapter: Chapter, lang?: string): PartialRegenerateRequest | null {
  const selectedText = chapter.content ?? ''
  if (!selectedText.trim()) return null
  return {
    start: 0,
    end: Array.from(selectedText).length,
    selected_text: selectedText,
    instruction: 'quality_regenerate',
    length_mode: 'similar',
    lang,
  }
}
